import { useEffect } from 'react';

import { ExamResultsCard } from '../exams/ExamResultsCard';
import { ExamSheetOverlay } from '../exams/ExamSheetOverlay';
import { useDynamicDocumentStore } from '../filmstrip/documents';
import { HomeworkResultsCard } from '../submissions/HomeworkResultsCard';
import { SubmissionNavigator } from '../submissions/SubmissionNavigator';
import { SubmissionOverlay } from '../submissions/SubmissionOverlay';
import { useHomeworkStudentView } from '../submissions/useSubmissionViewer';
import type { BoardContextValue } from '../useBoardContext';
import { activeSession, stepStudent } from './sessions';
import { useResultSessionStore } from './store';
import type { useResultActions } from './useResultActions';

export interface BoardResultsProps {
  context: BoardContextValue;
  actions: ReturnType<typeof useResultActions>;
  /** The đề's rail with Kết quả and Chi tiết is on screen for the board page. */
  railShown?: boolean;
}

// The results that follow the board page: the tab's own card, folded or not, and its student's work in place of the đề
export function BoardResults({ context, actions, railShown = false }: BoardResultsProps) {
  const sessions = useResultSessionStore((state) => state.sessions);
  const openStudent = useResultSessionStore((state) => state.openStudent);
  const selectItem = useResultSessionStore((state) => state.selectItem);
  const session = activeSession(sessions, context.unitId);
  const homework = useHomeworkStudentView(session, context.unitId);

  // sessions belong to one period's board; leaving it must not carry a class's work into the next
  useEffect(() => () => {
    useResultSessionStore.getState().clear();
    useDynamicDocumentStore.getState().clearTransient();
  }, [context.classId, context.date, context.index]);

  if (!session) return null;
  const tabId = session.tabId;
  const backToDe = () => actions.backToDe(tabId);
  const codes = homework.order.map((item) => item.code);
  // no rail for this tab (a cover, or a student's work in its place): the card switches views itself
  const switchable = !(railShown && tabId === context.unitId);

  return (
    <>
      {/* a closed card still leaves its student's work on the board */}
      {session.hidden ? null : session.kind === 'exam'
        ? <ExamResultsCard key={tabId} session={session} context={context} switchable={switchable} onBackToDe={backToDe} />
        : <HomeworkResultsCard key={tabId} session={session} switchable={switchable} onBackToDe={backToDe} />}
      {session.kind === 'exam' && session.student ? <ExamSheetOverlay session={session} onBackToDe={backToDe} /> : null}
      {homework.current && homework.task ? (
        <SubmissionNavigator
          taskTitle={homework.task.title}
          order={homework.order}
          current={homework.current}
          item={session.item}
          onStep={(delta) => { const code = stepStudent(codes, session.student, delta); if (code) openStudent(tabId, code); }}
          onSelectItem={(item) => selectItem(tabId, item)}
          onClose={backToDe}
        />
      ) : null}
      <SubmissionOverlay overlay={homework.overlay} onClose={homework.dismissOverlay} />
    </>
  );
}
